import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, DropdownButton, MenuItem, Table } from 'react-bootstrap';
import Time from 'react-time';
import { getOrgTermsContent } from '../../../content/utils.js';
import { EditTaskLink, ProjectLink, TaskLink } from '../../links/index.jsx';
import OwnersList from '../../OwnersList.jsx';
import { EyeIcon } from '../../icons/EyeIcon.jsx';
import { EditBlueIcon } from '../../icons/EditBlueIcon.jsx';
import { TriangleBlueIcon } from '../../icons/TriangleBlueIcon.jsx';
import styles from './TasksTable.module.css';

import Editor from '../../editor/editor.jsx';
import CustomMenuItem from '../../CustomMenuItem.jsx';

// Column sets. The project change impact page uses the default ("..." actions
// menu); the employee profile Tasks tab shows the project, combined start/end
// dates and a single "View" button.
export const TASK_COLUMNS = {
  DEFAULT: [
    'titleDescription',
    'owner',
    'status',
    'startDate',
    'endDate',
    'actions',
  ],
  EMPLOYEE_PROFILE: [
    'titleDescription',
    'project',
    'status',
    'dates',
    'owner',
    'viewAction',
  ],
};

export const DESCRIPTION_EXPAND = {
  // "Show More" links to the task page
  LINK: 'link',
  // "Show More" / "Show Less" toggles the full description in place
  INLINE: 'inline',
};

const COLUMN_HEADERS = {
  titleDescription: { label: 'Title & Description' },
  project: { label: () => getOrgTermsContent('project') },
  owner: { label: 'Owner' },
  status: { label: 'Status' },
  startDate: { label: 'Start Date' },
  endDate: { label: 'End Date' },
  dates: { label: 'Dates' },
  actions: { label: 'Actions', style: { textAlign: 'end' } },
  viewAction: { label: 'Action', style: { textAlign: 'end' } },
};

// changeImpactId / projectId / archived can be given per table (project page)
// or come from the task row itself (employee profile API).
export const TasksRow = ({
  task,
  changeImpactId: tableChangeImpactId,
  projectId: tableProjectId,
  manageProjectEntities: tableManageProjectEntities,
  archived: tableArchived,
  columns = TASK_COLUMNS.DEFAULT,
  showAllOwners = false,
  linkOwnersToEmployee = false,
  descriptionExpand = DESCRIPTION_EXPAND.LINK,
}) => {
  const changeImpactId = tableChangeImpactId ?? task.changeImpactId;
  const projectId = tableProjectId ?? task.project?.id;
  const manageProjectEntities = tableManageProjectEntities;
  const archived = tableArchived ?? task.project?.archived;

  const MAX_COUNT_OF_SYMBOLS_IN_DESCRIPTION = 100;
  const MAX_COUNT_OF_BLOCKS = 3;
  const parsedTaskDescription =
    task?.description && JSON.parse(task.description);
  const [taskDescription, setTaskDescription] = useState(
    parsedTaskDescription || null
  );
  const [editorInstance, setEditorInstance] = useState(null);
  const [isInitializedEditor, setIsInitializedEditor] = useState(false);
  const [isNeedToShowMore, setIsNeedToShowMore] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const isAvailableBlocksInTaskDescription = taskDescription?.blocks.length;

  const taskTableEditorOptions = useMemo(
    () => ({
      holder: `task-${task.id}-table-editor`,
      readOnly: true,
      minHeight: 0,
    }),
    [task.id]
  );

  const checkDescriptionLength = () => {
    if (isAvailableBlocksInTaskDescription) {
      let totalCountOfSymbol = 0;

      const filteredDescriptionBlocks = taskDescription.blocks
        .filter((block, index) => {
          const template = document.createElement('template');

          // parse task description
          if (['paragraph', 'header'].includes(block.type)) {
            template.innerHTML += block.data.text;
          } else if (['list'].includes(block.type)) {
            block.data.items.forEach((item) => {
              template.innerHTML += item;
            });
          } else if (['table'].includes(block.type)) {
            block.data.content.forEach((row) => {
              row.forEach((rowItem) => {
                template.innerHTML += rowItem;
              });
            });
          }

          // calculate symbols from the parsed description
          if ('content' in template) {
            totalCountOfSymbol += template.content.textContent.length;

            if (totalCountOfSymbol <= MAX_COUNT_OF_SYMBOLS_IN_DESCRIPTION) {
              return block;
            }
            if (
              index === 0 &&
              totalCountOfSymbol > MAX_COUNT_OF_SYMBOLS_IN_DESCRIPTION
            ) {
              return block;
            }
          }
        })
        .slice(0, MAX_COUNT_OF_BLOCKS);

      setTaskDescription({
        ...taskDescription,
        blocks: [...filteredDescriptionBlocks],
      });

      setIsNeedToShowMore(
        totalCountOfSymbol > MAX_COUNT_OF_SYMBOLS_IN_DESCRIPTION ||
          parsedTaskDescription.blocks.length > MAX_COUNT_OF_BLOCKS
      );
    }
  };

  useEffect(() => {
    checkDescriptionLength();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    editorInstance &&
      editorInstance.hasOwnProperty('isReady') &&
      editorInstance.isReady.then(() => {
        setIsInitializedEditor(true);
      });
  }, [editorInstance]);

  const cells = {
    titleDescription: () => (
      <td>
        <strong>{task.title}</strong>
        <br />

        {isAvailableBlocksInTaskDescription ? (
          <div className={styles.taskTableEditorContainer}>
            <Editor
              options={taskTableEditorOptions}
              data={taskDescription}
              // the editor always holds the full description; the preview is a
              // CSS line clamp, so expanding inline does not re-mount EditorJS
              isNeedToSetLimitOfLinesNumber={
                isNeedToShowMore && !isDescriptionExpanded
              }
              setEditorInstanceForParent={setEditorInstance}
            />
          </div>
        ) : null}

        {isNeedToShowMore &&
        isInitializedEditor &&
        descriptionExpand === DESCRIPTION_EXPAND.INLINE ? (
          <button
            type="button"
            className="btn-clear bg-transparent btn-link flex"
            onClick={() => setIsDescriptionExpanded((expanded) => !expanded)}
            style={{ paddingLeft: 0 }}
          >
            <span className="m-r-8">
              {isDescriptionExpanded ? 'Show Less' : 'Show More'}
            </span>
            <TriangleBlueIcon
              style={{ height: '20px' }}
              transform={`rotate(${isDescriptionExpanded ? '0' : '180'})`}
            />
          </button>
        ) : null}

        {isNeedToShowMore &&
        isInitializedEditor &&
        descriptionExpand === DESCRIPTION_EXPAND.LINK ? (
          <TaskLink
            taskId={task.id}
            changeImpactId={changeImpactId}
            projectId={projectId}
          >
            Show More
          </TaskLink>
        ) : null}
      </td>
    ),
    owner: () => (
      <td>
        {task.owners.length ? (
          <OwnersList
            owners={task.owners}
            showAll={showAllOwners}
            linkToEmployee={linkOwnersToEmployee}
          />
        ) : (
          '—'
        )}
      </td>
    ),
    status: () => (
      <td>
        <strong>{task.status || '—'}</strong>
      </td>
    ),
    startDate: () => (
      <td>
        <strong>
          {task.startDate ? (
            <Time
              value={task.startDate}
              format="M/D/YY"
              className="text-muted"
            />
          ) : (
            '—'
          )}
        </strong>
      </td>
    ),
    endDate: () => (
      <td>
        <strong>
          {task.endDate ? (
            <Time value={task.endDate} format="M/D/YY" className="text-muted" />
          ) : (
            '—'
          )}
        </strong>
      </td>
    ),
    dates: () => (
      <td>
        <strong className="flex flex-col flex-align-start">
          {task.startDate || task.endDate ? (
            <React.Fragment>
              <span>
                {task.startDate ? (
                  <Time value={task.startDate} format="M/D/YY" />
                ) : (
                  '—'
                )}
              </span>
              <span>
                {task.endDate ? (
                  <Time value={task.endDate} format="M/D/YY" />
                ) : (
                  '—'
                )}
              </span>
            </React.Fragment>
          ) : (
            '—'
          )}
        </strong>
      </td>
    ),
    project: () => (
      <td>
        {task.project ? (
          <ProjectLink projectId={task.project.id}>
            {task.project.name}
          </ProjectLink>
        ) : (
          '—'
        )}
      </td>
    ),
    viewAction: () => (
      <td className="text-right">
        <TaskLink
          taskId={task.id}
          changeImpactId={changeImpactId}
          projectId={projectId}
        >
          <Button bsStyle="primary">View</Button>
        </TaskLink>
      </td>
    ),
    actions: () => (
      <td>
        <div className="flex flex-end">
          <div className="actions">
            <DropdownButton
              noCaret
              title=""
              id="id"
              className="btn-more btn btn-default"
            >
              <TaskLink
                taskId={task.id}
                changeImpactId={changeImpactId}
                projectId={projectId}
              >
                <span className="item">
                  <CustomMenuItem>
                    <span className="flex">
                      <EyeIcon />
                      <span>&nbsp; View</span>
                    </span>
                  </CustomMenuItem>
                  <MenuItem divider />
                </span>
              </TaskLink>

              {manageProjectEntities ? (
                archived ? (
                  <span className="item">
                    <CustomMenuItem
                      onClick={() =>
                        window.alert(
                          getOrgTermsContent('archivedProjectMessage')
                        )
                      }
                    >
                      <span className="flex">
                        <EditBlueIcon />
                        <span>&nbsp; Edit</span>
                      </span>
                    </CustomMenuItem>
                    <MenuItem divider />
                  </span>
                ) : (
                  <EditTaskLink
                    taskId={task.id}
                    changeImpactId={changeImpactId}
                    projectId={projectId}
                  >
                    <span className="item">
                      <CustomMenuItem>
                        <span className="flex">
                          <EditBlueIcon />
                          <span>&nbsp; Edit</span>
                        </span>
                      </CustomMenuItem>
                    </span>
                  </EditTaskLink>
                )
              ) : null}
            </DropdownButton>
          </div>
        </div>
      </td>
    ),
  };

  return (
    <tr key={task.id}>
      {columns.map((column) => (
        <React.Fragment key={column}>{cells[column]()}</React.Fragment>
      ))}
    </tr>
  );
};

const TasksTable = ({
  tasks,
  changeImpactId,
  projectId,
  manageProjectEntities,
  archived,
  columns = TASK_COLUMNS.DEFAULT,
  showAllOwners = false,
  // owner names link to the employee profile (employee profile Tasks tab)
  linkOwnersToEmployee = false,
  descriptionExpand = DESCRIPTION_EXPAND.LINK,
  // the project page lists tasks newest first; API-ordered lists pass false
  reverse = true,
}) =>
  tasks?.length ? (
    <Table hover className={styles.tasksTable}>
      <thead>
        <tr>
          {columns.map((column) => {
            const { label, style } = COLUMN_HEADERS[column];
            return (
              <th key={column} style={style}>
                {typeof label === 'function' ? label() : label}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {(reverse ? tasks.slice().reverse() : tasks).map((task) => (
          <TasksRow
            key={task.id}
            task={task}
            changeImpactId={changeImpactId}
            projectId={projectId}
            manageProjectEntities={manageProjectEntities}
            archived={archived}
            columns={columns}
            showAllOwners={showAllOwners}
            linkOwnersToEmployee={linkOwnersToEmployee}
            descriptionExpand={descriptionExpand}
          />
        ))}
      </tbody>
    </Table>
  ) : null;

TasksTable.propTypes = {
  task: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
    })
  ),
};

export default TasksTable;
